// Angular:
import {
    Directive,
    ElementRef,
    forwardRef,
    HostListener,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';

// Formats the given number with thousand separators and two decimal places
function formatNumeric(value: number): string {
    if (!value && value !== 0) {
        return '';
    }
    const fractionDigit = value % 1 === 0 ? 0 : 2; // checking if the price is whole number
    const parts = value.toFixed(fractionDigit).split('.', 2);
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}

function isNullOrUndefined(value: any): value is null | undefined {
    return value === undefined || value === null;
}

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'input[tgTextInput][wholeNumber]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // eslint-disable-next-line @angular-eslint/no-forward-ref
            useExisting: forwardRef(() => WholeNumberDirective),
            multi: true
        }
    ],
    standalone: false
})
export class WholeNumberDirective
    implements ControlValueAccessor, OnDestroy, OnInit {
    private _destroyed$ = new Subject();

    private _onChangedHandlers: Array<(value: any) => void> = [];
    private _onTouchedHandlers: Array<() => void> = [];

    public currentValue: number | null = null;
    public currentText: string = null;

    constructor(
        private _elementRef: ElementRef,
        private _renderer: Renderer2
    ) { }

    public ngOnInit(): void {
        this.useNativeElement(this._elementRef, (input) => {
            // HACK: Set 'tel' type to trigger the numeric keyboard on non-iOs mobile devices, allows non-numeric strings
            (input as HTMLInputElement).type = 'tel';
        });
    }

    public ngOnDestroy(): void {
        this._destroyed$.next(true);
        this._onChangedHandlers.length = 0;
        this._onTouchedHandlers.length = 0;
    }

    @HostListener('keypress', ['$event'])
    public onKeyPress(event: KeyboardEvent): void {
        if (event.key !== '0' && !parseInt(event.key, 10)) {
            event.preventDefault();
        }
    }

    @HostListener('change')
    @HostListener('keyup')
    public onChange(): void {
        this.currentValue = this.parseText(this.getInputText());
        this.publishCurrentValueChanged();
        this.publishInputTouched();
        this.tryUpdateInputTextFromCurrentValue();
    }

    private publishCurrentValueChanged(): void {
        this._onChangedHandlers.forEach((fn) => fn(this.currentValue));
    }

    private publishInputTouched(): void {
        this._onTouchedHandlers.forEach((fn) => fn());
    }

    private getInputText(): string {
        return (
            this.useNativeElement(
                this._elementRef,
                (el) => (el as HTMLInputElement).value
            ) || ''
        );
    }

    private tryUpdateInputTextFromCurrentValue(): boolean {
        const value = this.currentValue;
        let formattedText: string;

        if (isNaN(value)) {
            formattedText = '';
        } else if (value !== null && typeof value === 'number') {
            formattedText = formatNumeric(value);
        } else {
            formattedText = (value || '').toString();
        }

        const hasChanged = this.currentText !== formattedText;

        if (hasChanged) {
            this.currentText = formattedText;

            this.useNativeElement(this._elementRef, (input) => {
                (input as HTMLInputElement).value = formattedText;
            });
        }

        return hasChanged;
    }

    private parseText(text: string): number | null {
        // NOTE: Empty input is technically valid (no input)
        if (!text || /^\s*$/.test(text)) {
            return null;
        }

        // NOTE: parseFloat() ignores trailing nonsense. Make sure we're actually looking at something that might be
        // a sensible number before parsing. Allow misplaced commas, random whitespace, etc.
        // NOTE: onBlur will automatically reformat the number
        if (!/^[0-9.,\-\s]+$/.test(text)) {
            return NaN;
        }

        // NOTE: Ignore thousand separators and whitespace
        return parseFloat(text.replace(/[,\s]+/g, ''));
    }

    private trySetCurrentValue(value: number | null): boolean {
        const isValueUnchanged =
            this.currentValue === value ||
            (isNullOrUndefined(this.currentValue) &&
                isNullOrUndefined(value)) ||
            (isNaN(this.currentValue) && isNaN(value));

        if (isValueUnchanged) {
            return false;
        }

        this.currentValue = value;
        return true;
    }

    private useNativeElement<T>(
        el: ElementRef,
        handler: (nativeElement: HTMLElement) => T
    ): T | null {
        if (el && el.nativeElement) {
            return handler(el.nativeElement);
        }
        return null;
    }

    /* ControlValueAccessor */

    public writeValue(value: any): void {
        if (
            this.trySetCurrentValue(value) &&
            this.tryUpdateInputTextFromCurrentValue()
        ) {
            // Currently we avoid publishing values "on write". The value of this
            // input may have changed if the auto-format routine rounded to two decimals. This may be safe to do if the
            // value is still a number?
        }
    }

    public registerOnChange(fn: (value: any) => void): void {
        this._onChangedHandlers.push(fn);
    }

    public registerOnTouched(fn: () => void): void {
        this._onTouchedHandlers.push(fn);
    }

    public setDisabledState(_isDisabled: boolean): void {
        this._renderer.setProperty(
            this._elementRef.nativeElement,
            'disabled',
            _isDisabled
        );
    }
}
